import { useEffect, useState } from 'react';
import Input from '../ui/Input';
import Select from '../ui/Select';

/**
 * The dynamic-field builder row — shared by the workflow "Edit step" modal and the
 * Nationalities master modal. Renders one field definition with its type, options and
 * validation controls, plus (for options fields) any conditional fields nested under each
 * answer. Pairs with <DynamicField /> / <FieldAnswerList /> which render the fields for
 * answering.
 *
 * Fields are kept as one flat array (`fields`); a conditional field just carries a
 * `depends_on: { field_id, value }` pointing at the option that reveals it. `onFieldsChange`
 * always receives the whole updated array back — this component owns finding/mutating a
 * field's descendants (cascading a delete, a type change, or an option rename/removal) so
 * callers never have to know about the nesting.
 *
 * A workflow step's required documents can be conditional the same way: pass `documents` +
 * `onDocumentsChange` (the step's required_documents array/setter) and `docTypeOptions` (the
 * document types available to pick from) to let each option also reveal a required document,
 * not just a field. Omitting `documents` hides that UI entirely (e.g. the Nationalities master,
 * which has its own separate, unconditional documents list).
 */

export const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'checkbox', label: 'Check box' },
  { value: 'radio', label: 'Radio button' },
  { value: 'select', label: 'Select field' },
  { value: 'multiselect', label: 'Multi select field' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
];

export const OPTION_TYPES = ['checkbox', 'radio', 'select', 'multiselect'];

export const DATE_RULE_OPERATORS = [
  { value: 'before', label: 'Less than' },
  { value: 'after', label: 'Greater than' },
];

export const DATE_RULE_REFERENCES = [
  { value: 'today', label: 'Today' },
  { value: '1_week', label: 'One week from today' },
  { value: '1_month', label: 'One month from today' },
  { value: '1_year', label: 'One year from today' },
];

/** Fixed registration fields a step field can mirror — keys match api/app/Support/RegistrationLinks.php. */
export const REGISTRATION_LINK_OPTIONS = [
  { value: 'full_name', label: 'Full name', type: 'text' },
  { value: 'email', label: 'Email', type: 'text' },
  { value: 'contact_no', label: 'Contact no.', type: 'text' },
  { value: 'whatsapp_no', label: 'WhatsApp no.', type: 'text' },
  { value: 'home_country_contact_no', label: 'Home country contact no.', type: 'text' },
  { value: 'nationality', label: 'Nationality', type: 'text' },
  { value: 'date_of_birth', label: 'Date of birth', type: 'date' },
  { value: 'passport_no', label: 'Passport no.', type: 'text' },
  { value: 'passport_expiry_date', label: 'Passport expiry date', type: 'date' },
  { value: 'old_emirates_id_no', label: 'Old Emirates ID no.', type: 'text' },
  { value: 'referred_agent_name', label: 'Referred agent name', type: 'text' },
  { value: 'rta_permit_details', label: 'RTA permit details', type: 'text' },
  { value: 'old_visa', label: 'Old visa', type: 'text' },
  { value: 'old_visa_remark', label: 'Old visa remark', type: 'text' },
  { value: 'visa_type', label: 'Visa type', type: 'text' },
  { value: 'visa_validity', label: 'Visa validity', type: 'date' },
  { value: 'uid_no', label: 'UID no.', type: 'text' },
  { value: 'uae_license_no', label: 'UAE licence no.', type: 'text' },
  { value: 'uae_license_expiry', label: 'UAE licence expiry', type: 'date' },
  { value: 'home_license_no', label: 'Home licence no.', type: 'text' },
  { value: 'home_license_expiry', label: 'Home licence expiry', type: 'date' },
];

export const FORMAT_KINDS = [
  { value: 'letters', label: 'Letters (A–Z)', sample: 'abcdefghijklmnopqrstuvwxyz' },
  { value: 'digits', label: 'Digits (0–9)', sample: '0123456789' },
  { value: 'alphanumeric', label: 'Letters or digits', sample: 'a1b2c3d4e5f6g7h8i9j0' },
  { value: 'literal', label: 'Separator / fixed text' },
];

/** A sample answer that matches the format, e.g. "abc/12". */
export function formatExample(format) {
  return (format?.segments || [])
    .map((seg) => {
      if (seg.kind === 'literal') return seg.value || '?';
      const sample = FORMAT_KINDS.find((k) => k.value === seg.kind).sample;
      return sample.repeat(Math.ceil((seg.length || 1) / sample.length)).slice(0, seg.length || 1);
    })
    .join('');
}

/** Client-side mirror of DynamicFields::formatRegex() in the API. */
export function matchesFormat(format, value) {
  const esc = (t) => t.replace(/[.*+?^${}()|[\]\\/-]/g, '\\$&');
  const body = format.segments
    .map((seg) => {
      if (seg.kind === 'literal') return esc(seg.value || '');
      const cls = { letters: 'A-Za-z', digits: '0-9', alphanumeric: 'A-Za-z0-9' }[seg.kind];
      return `[${cls}]{${seg.length || 1}}`;
    })
    .join('');
  return new RegExp(`^${body}$`).test(String(value).trim());
}

function FormatBuilder({ format, onChange }) {
  const segments = format?.segments || [];
  const set = (next) => onChange(next.length ? { segments: next } : null);
  const update = (idx, patch) => set(segments.map((seg, i) => (i === idx ? { ...seg, ...patch } : seg)));
  const changeKind = (idx, kind) => set(segments.map((seg, i) => (i === idx ? (kind === 'literal' ? { kind, value: '/' } : { kind, length: seg.length || 2 }) : seg)));
  const move = (idx, dir) => {
    const next = [...segments];
    const [item] = next.splice(idx, 1);
    next.splice(idx + dir, 0, item);
    set(next);
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">Build the answer pattern part by part, left to right. Leave empty to accept any text.</p>
      {segments.map((seg, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="w-5 shrink-0 text-center text-xs text-gray-400">{idx + 1}</span>
          <Select className="w-48 shrink-0" value={seg.kind} onChange={(e) => changeKind(idx, e.target.value)}>
            {FORMAT_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </Select>
          {seg.kind === 'literal' ? (
            <Input className="w-28" placeholder="e.g. /" maxLength={10} value={seg.value} onChange={(e) => update(idx, { value: e.target.value })} />
          ) : (
            <div className="flex items-center gap-1.5">
              <Input className="w-20" type="number" min={1} max={50} value={seg.length} onChange={(e) => update(idx, { length: Math.max(1, Math.min(50, Number(e.target.value) || 1)) })} />
              <span className="text-xs text-gray-500">character{seg.length === 1 ? '' : 's'}</span>
            </div>
          )}
          <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} title="Move left" className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">
            ▲
          </button>
          <button type="button" disabled={idx === segments.length - 1} onClick={() => move(idx, 1)} title="Move right" className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">
            ▼
          </button>
          <button type="button" onClick={() => set(segments.filter((_, i) => i !== idx))} className="text-xs text-gray-400 hover:text-red-600">
            ✕
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => set([...segments, { kind: 'letters', length: 3 }])} className="h-8 rounded-md border border-primary px-3 text-xs font-medium text-primary hover:bg-primary-50">
          + Add part
        </button>
        {segments.length > 0 && (
          <span className="text-xs text-gray-600">
            Example answer: <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-800">{formatExample(format)}</code>
          </span>
        )}
      </div>
    </div>
  );
}

/** State + buttons for the "Expand all / Collapse all" control above a list of FieldEditors. */
export function useCollapseAll() {
  const [signal, setSignal] = useState({ collapsed: false, n: 0 });
  return [signal, (collapsed) => setSignal((s) => ({ collapsed, n: s.n + 1 }))];
}

export function CollapseAllButtons({ onSet }) {
  return (
    <>
      <button type="button" onClick={() => onSet(false)} className="text-xs text-gray-600 hover:underline">
        Expand all
      </button>
      <button type="button" onClick={() => onSet(true)} className="text-xs text-gray-600 hover:underline">
        Collapse all
      </button>
    </>
  );
}

export function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `f_${Date.now()}_${Math.random()}`;
}

export function emptyField() {
  return {
    id: newId(),
    label: '',
    type: 'text',
    options: [],
    validation: { required: false, date_rule: null, format: null },
    show_in_basic_details: false,
    linked_registration_field: null,
    depends_on: null,
    linked_step_field: null,
    attempt_field: false,
    notify_options: [],
  };
}

function childFieldIds(fields, parentId) {
  const direct = fields.filter((f) => f.depends_on?.field_id === parentId).map((f) => f.id);
  return direct.reduce((acc, id) => [...acc, ...childFieldIds(fields, id)], direct);
}

/** Remove a field and, recursively, every conditional field nested under it. */
function removeFieldCascade(fields, id) {
  const doomed = new Set([id, ...childFieldIds(fields, id)]);
  return fields.filter((f) => !doomed.has(f.id));
}

/** Top-level "+ Add field" — always a plain, unconditional field. */
export function addTopLevelField(fields) {
  return [...fields, emptyField()];
}

/**
 * Move a top-level field one place up/down among its siblings (same section_id) by swapping it
 * with the neighbouring sibling in the flat array. Returns the same array if it can't move.
 */
export function moveTopLevelField(fields, id, dir) {
  const self = fields.find((f) => f.id === id);
  if (!self) return fields;
  const siblings = fields.filter((f) => !f.depends_on && (f.section_id || null) === (self.section_id || null));
  const pos = siblings.findIndex((f) => f.id === id);
  const other = siblings[pos + dir];
  if (!other) return fields;
  const a = fields.indexOf(self);
  const b = fields.indexOf(other);
  const next = [...fields];
  next[a] = other;
  next[b] = self;
  return next;
}

/** ▲ ▼ buttons to reorder a top-level field. */
export function MoveButtons({ fields, field, onFieldsChange }) {
  const siblings = fields.filter((f) => !f.depends_on && (f.section_id || null) === (field.section_id || null));
  const pos = siblings.findIndex((f) => f.id === field.id);
  const btn = 'flex h-5 w-5 items-center justify-center rounded border border-gray-300 bg-white text-[10px] text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30';
  return (
    <div className="mt-2 flex shrink-0 flex-col gap-1">
      <button type="button" title="Move up" disabled={pos <= 0} onClick={() => onFieldsChange(moveTopLevelField(fields, field.id, -1))} className={btn}>
        ▲
      </button>
      <button type="button" title="Move down" disabled={pos < 0 || pos >= siblings.length - 1} onClick={() => onFieldsChange(moveTopLevelField(fields, field.id, 1))} className={btn}>
        ▼
      </button>
    </div>
  );
}

/**
 * "Link to another step's field" — pick a step, then one of its fields, then whether the linked
 * field is read-only (shown disabled) or updatable (edits are written back to the source field).
 */
const REGISTRATION_SOURCE = 'registration';

function StepLinkPanel({ field, sources, registrationOptions, blocked, onChange }) {
  const link = field.linked_step_field;
  // '' (not null) is the "Registration chosen, field not yet picked" placeholder; the API drops it.
  const regLink = field.linked_registration_field;
  const isReg = regLink !== null && regLink !== undefined;
  const linkable = (step) => (step.fields || []).filter((f) => !f.linked_step_field && !f.linked_registration_field);
  const steps = sources.filter((st) => linkable(st).length > 0);
  const step = link ? sources.find((st) => st.step_definition_id === link.step_definition_id) : null;
  const sourceField = step ? (step.fields || []).find((f) => f.id === link.field_id) : null;

  const pickStep = (id) => {
    if (!id) return onChange({ ...field, linked_step_field: null, linked_registration_field: null });
    if (id === REGISTRATION_SOURCE) return onChange({ ...field, linked_step_field: null, linked_registration_field: '' });
    // Step chosen, field not yet — keep a placeholder link so the field select appears.
    onChange({ ...field, linked_registration_field: null, linked_step_field: { step_definition_id: Number(id), field_id: '', updatable: link?.updatable ?? false } });
  };

  const pickRegField = (key) => {
    const opt = (registrationOptions || []).find((o) => o.value === key);
    if (!opt) return onChange({ ...field, linked_registration_field: '' });
    // The registration field decides the type (text or date); options and text format don't apply.
    onChange({
      ...field,
      type: opt.type,
      options: [],
      linked_step_field: null,
      linked_registration_field: key,
      validation: { ...field.validation, format: null, date_rule: opt.type === 'date' ? field.validation.date_rule : null },
    });
  };

  const pickField = (fieldId) => {
    const src = (step?.fields || []).find((f) => f.id === fieldId);
    if (!src) return onChange({ ...field, linked_step_field: { ...link, field_id: '' } });
    onChange({
      ...field,
      type: src.type,
      options: src.options ? [...src.options] : [],
      linked_registration_field: null,
      validation: { ...field.validation, format: null, date_rule: src.type === 'date' ? field.validation.date_rule : null },
      linked_step_field: { ...link, field_id: fieldId },
    });
  };

  return (
    <div className="mt-3 space-y-2 border-t border-gray-300 pt-2.5 pl-1">
      <p className="text-xs font-medium text-gray-500">Link to another step's field (optional)</p>
      {blocked && !link && !isReg ? (
        <p className="text-xs text-gray-400">Remove this field's conditional fields and documents first to link it.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <Select className="w-56" value={isReg ? REGISTRATION_SOURCE : link?.step_definition_id ?? ''} onChange={(e) => pickStep(e.target.value)}>
              <option value="">Not linked — filled in manually</option>
              {registrationOptions && <option value={REGISTRATION_SOURCE}>Registration</option>}
              {steps.map((st) => (
                <option key={st.step_definition_id} value={st.step_definition_id}>
                  {st.title}
                </option>
              ))}
            </Select>
            {isReg && (
              <Select className="w-56" value={regLink || ''} onChange={(e) => pickRegField(e.target.value)}>
                <option value="">Select field…</option>
                {registrationOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label} ({FIELD_TYPES.find((t) => t.value === o.type)?.label || o.type})
                  </option>
                ))}
              </Select>
            )}
            {link && (
              <Select className="w-56" value={link.field_id || ''} onChange={(e) => pickField(e.target.value)}>
                <option value="">Select field…</option>
                {(step ? linkable(step) : []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label} ({FIELD_TYPES.find((t) => t.value === f.type)?.label || f.type})
                  </option>
                ))}
              </Select>
            )}
          </div>

          {isReg && regLink && (
            <p className="text-xs text-gray-400">
              This field is filled automatically from the candidate's registration data and can't be edited here. Type follows the registration field.
            </p>
          )}

          {link && link.field_id && (
            <div className="space-y-1">
              {[
                { value: false, label: 'Read-only', hint: 'Shows the linked field’s answer in a disabled input.' },
                { value: true, label: 'Updatable', hint: 'Starts with the linked field’s answer and can be edited — changes are saved on this step only, not on the linked field.' },
              ].map((m) => (
                <label key={String(m.value)} className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    className="mt-1 accent-primary"
                    checked={!!link.updatable === m.value}
                    onChange={() => onChange({ ...field, linked_step_field: { ...link, updatable: m.value } })}
                  />
                  <span>
                    <span className="font-medium">{m.label}</span>
                    <span className="block text-xs text-gray-500">{m.hint}</span>
                  </span>
                </label>
              ))}
              {sourceField && (
                <p className="text-xs text-gray-400">
                  Mirrors “{sourceField.label}” from {step.title}. Type and answer options follow that field.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function FieldEditor({
  field,
  fields,
  onFieldsChange,
  documents = null,
  onDocumentsChange = null,
  docTypeOptions = [],
  registrationLinkOptions = null,
  stepLinkSources = null,
  attemptEnabled = false,
  showBasicDetailsOption = true,
  depth = 0,
  collapseSignal = null,
}) {
  // Existing fields start collapsed (name + type only); a brand-new blank field opens for editing.
  const [collapsed, setCollapsed] = useState(() => !!field.label.trim());
  // "Expand all / Collapse all" from the parent: each click bumps `collapseSignal.n`.
  useEffect(() => {
    if (collapseSignal && collapseSignal.n > 0) setCollapsed(collapseSignal.collapsed);
  }, [collapseSignal]);
  const stepLink = field.linked_step_field || null;
  const hasOptions = OPTION_TYPES.includes(field.type);
  const isDate = field.type === 'date';
  const hasDateRule = isDate && !!field.validation.date_rule;
  const children = fields.filter((f) => f.depends_on?.field_id === field.id);
  const hasDocuments = Array.isArray(documents);
  const docsOnThisField = hasDocuments ? documents.filter((d) => d.depends_on?.field_id === field.id) : [];

  const replaceSelf = (next) => onFieldsChange(fields.map((f) => (f.id === field.id ? next : f)));
  const removeSelf = () => {
    onFieldsChange(removeFieldCascade(fields, field.id));
    if (hasDocuments) {
      // Every field this deletion cascades away (this one plus its conditional descendants) can
      // no longer be depended on — drop any document conditioned on one of them too.
      const doomedFieldIds = new Set([field.id, ...childFieldIds(fields, field.id)]);
      onDocumentsChange(documents.filter((d) => !doomedFieldIds.has(d.depends_on?.field_id)));
    }
  };

  const setType = (type) => {
    const stillHasOptions = OPTION_TYPES.includes(type);
    // Switching away from an options type orphans anything conditioned on this field's old
    // options — drop that whole branch rather than leave dangling conditional fields.
    const withoutOrphans = stillHasOptions ? fields : fields.filter((f) => !childFieldIds(fields, field.id).includes(f.id));
    onFieldsChange(
      withoutOrphans.map((f) =>
        f.id === field.id
          ? {
              ...field,
              type,
              linked_registration_field: null,
              notify_options: stillHasOptions ? field.notify_options || [] : [],
              options: stillHasOptions ? (field.options.length ? field.options : ['', '']) : [],
              validation: {
                required: field.validation.required,
                date_rule: type === 'date' ? field.validation.date_rule : null,
                format: type === 'text' ? field.validation.format ?? null : null,
              },
            }
          : f
      )
    );
    if (hasDocuments && !stillHasOptions) {
      onDocumentsChange(documents.filter((d) => d.depends_on?.field_id !== field.id));
    }
  };

  const setOption = (idx, value) => {
    const oldValue = field.options[idx];
    const options = [...field.options];
    options[idx] = value;
    const notify_options = (field.notify_options || []).map((o) => (o === oldValue ? value : o));
    const next = fields.map((f) => (f.id === field.id ? { ...field, options, notify_options } : f));
    // Keep conditional fields pointed at the right option when its label changes.
    onFieldsChange(
      next.map((f) => (f.depends_on?.field_id === field.id && f.depends_on.value === oldValue ? { ...f, depends_on: { ...f.depends_on, value } } : f))
    );
    if (hasDocuments) {
      onDocumentsChange(
        documents.map((d) => (d.depends_on?.field_id === field.id && d.depends_on.value === oldValue ? { ...d, depends_on: { ...d.depends_on, value } } : d))
      );
    }
  };

  const addOption = () => replaceSelf({ ...field, options: [...field.options, ''] });

  const removeOption = (idx) => {
    const value = field.options[idx];
    const withoutOption = fields.map((f) =>
      f.id === field.id ? { ...field, options: field.options.filter((_, i) => i !== idx), notify_options: (field.notify_options || []).filter((o) => o !== value) } : f
    );
    // The option is gone, so any field conditioned on it is now orphaned — drop those too.
    const orphans = withoutOption.filter((f) => f.depends_on?.field_id === field.id && f.depends_on.value === value);
    onFieldsChange(orphans.reduce((acc, orphan) => removeFieldCascade(acc, orphan.id), withoutOption));
    if (hasDocuments) {
      onDocumentsChange(documents.filter((d) => !(d.depends_on?.field_id === field.id && d.depends_on.value === value)));
    }
  };

  const addChildField = (optionValue) => onFieldsChange([...fields, { ...emptyField(), depends_on: { field_id: field.id, value: optionValue } }]);

  const availableDocTypes = docTypeOptions.filter((t) => !(documents || []).some((d) => d.code === t.code));
  const docTypeName = (code) => docTypeOptions.find((t) => t.code === code)?.name || code;
  const addDoc = (optionValue, code) => {
    if (!code || !hasDocuments || documents.some((d) => d.code === code)) return;
    onDocumentsChange([...documents, { code, required: true, verify: true, depends_on: { field_id: field.id, value: optionValue } }]);
  };
  const removeDoc = (code) => onDocumentsChange(documents.filter((d) => d.code !== code));
  const setDocOption = (code, patch) => onDocumentsChange(documents.map((d) => (d.code === code ? { ...d, ...patch } : d)));

  const setRequired = (required) => replaceSelf({ ...field, validation: { ...field.validation, required } });

  const toggleDateRule = (enabled) =>
    replaceSelf({
      ...field,
      validation: { ...field.validation, date_rule: enabled ? { operator: 'after', reference: 'today' } : null },
    });

  const setDateRule = (patch) =>
    replaceSelf({ ...field, validation: { ...field.validation, date_rule: { ...field.validation.date_rule, ...patch } } });

  return (
    <div
      className={
        depth
          ? 'rounded-md border border-gray-300 border-l-4 border-l-gray-400 bg-gray-50 p-3 shadow-sm'
          : 'rounded-lg border border-gray-300 border-l-4 border-l-primary bg-white p-3.5 shadow-md'
      }
    >
      {collapsed ? (
        <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand' : 'Collapse'}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-xs text-gray-600 hover:bg-gray-100"
        >
          {collapsed ? '▸' : '▾'}
        </button>
          <button type="button" onClick={() => setCollapsed(false)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
            <span className="truncate text-sm font-medium text-gray-800">{field.label || 'Untitled field'}</span>
            <span className="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-700">
              {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
            </span>
            {field.validation.required && <span className="shrink-0 text-xs font-semibold text-primary">Required</span>}
            {hasOptions && <span className="shrink-0 text-xs text-gray-400">{field.options.filter((o) => o.trim()).length} answers</span>}
            {children.length + docsOnThisField.length > 0 && (
              <span className="shrink-0 text-xs text-gray-400">· {children.length + docsOnThisField.length} conditional</span>
            )}
          </button>
          <button type="button" onClick={removeSelf} className="shrink-0 text-xs text-red-500 hover:underline">
            Remove
          </button>
        </div>
      ) : (
        <>
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand' : 'Collapse'}
          className="mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-xs text-gray-600 hover:bg-gray-100"
        >
          {collapsed ? '▸' : '▾'}
        </button>
        <Input
          className="flex-1"
          placeholder="Field label"
          value={field.label}
          onChange={(e) => replaceSelf({ ...field, label: e.target.value })}
        />
        <Select className="w-44 shrink-0" value={field.type} disabled={!!stepLink || !!field.linked_registration_field} title={stepLink || field.linked_registration_field ? 'Type follows the linked field' : undefined} onChange={(e) => setType(e.target.value)}>
          {FIELD_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <button type="button" onClick={removeSelf} className="mt-2 shrink-0 text-xs text-red-500 hover:underline">
          Remove
        </button>
      </div>


      {hasOptions && stepLink && (
        <p className="mt-2 pl-1 text-xs text-gray-500">Answer options come from the linked field: {field.options.join(', ')}.</p>
      )}

      {hasOptions && !stepLink && (
        <div className="mt-3 space-y-3 pl-1">
          <p className="text-xs font-medium text-gray-500">Answer options</p>
          {field.options.map((opt, idx) => {
            const optionChildren = children.filter((c) => c.depends_on.value === opt);
            return (
              <div key={idx} className="space-y-2 rounded-md border border-gray-300 border-l-4 border-l-primary-dark bg-gray-50 p-2.5">
                <div className="flex items-center gap-2">
                  <span className="shrink-0 rounded bg-gray-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">Answer {idx + 1}</span>
                  <Input className="flex-1" placeholder={`Option ${idx + 1}`} value={opt} onChange={(e) => setOption(idx, e.target.value)} />
                  {opt.trim() && (
                    <label className="flex shrink-0 items-center gap-1 text-xs text-gray-600" title="Notify the user who submits the step with this answer selected">
                      <input
                        type="checkbox"
                        className="accent-primary"
                        checked={(field.notify_options || []).includes(opt)}
                        onChange={(e) =>
                          replaceSelf({
                            ...field,
                            notify_options: e.target.checked
                              ? [...(field.notify_options || []), opt]
                              : (field.notify_options || []).filter((o) => o !== opt),
                          })
                        }
                      />
                      Notify
                    </label>
                  )}
                  {field.options.length > 2 && (
                    <button type="button" onClick={() => removeOption(idx)} title="Remove answer" className="text-xs text-gray-400 hover:text-red-600">
                      ✕
                    </button>
                  )}
                </div>
                {opt.trim() && (
                  <div className="space-y-2 rounded-md border border-dashed border-gray-300 bg-white p-2.5">
                    <p className="text-xs font-semibold text-gray-600">When “{opt}” is selected, also ask for:</p>
                    {optionChildren.map((child) => (
                      <FieldEditor
                        key={child.id}
                        field={child}
                        fields={fields}
                        onFieldsChange={onFieldsChange}
                        documents={documents}
                        onDocumentsChange={onDocumentsChange}
                        docTypeOptions={docTypeOptions}
                        registrationLinkOptions={registrationLinkOptions}
                        stepLinkSources={stepLinkSources}
                        attemptEnabled={attemptEnabled}
                        showBasicDetailsOption={showBasicDetailsOption}
                        depth={depth + 1}
                        collapseSignal={collapseSignal}
                      />
                    ))}
                    {hasDocuments && docsOnThisField.some((d) => d.depends_on.value === opt) && (
                      <div className="space-y-1.5">
                        {docsOnThisField
                          .filter((d) => d.depends_on.value === opt)
                          .map((doc) => (
                            <div key={doc.code} className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50/60 px-2 py-1.5">
                              <span className="flex items-center gap-2 text-xs text-gray-700">
                                <span className="rounded bg-primary-light px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary-dark">Document</span>
                                {docTypeName(doc.code)}
                              </span>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1 text-[11px] text-gray-500">
                                  <input
                                    type="checkbox"
                                    className="accent-primary"
                                    checked={doc.required}
                                    onChange={(e) => setDocOption(doc.code, { required: e.target.checked })}
                                  />
                                  Required
                                </label>
                                {doc.required && (
                                  <label className="flex items-center gap-1 text-[11px] text-gray-500">
                                    <input
                                      type="checkbox"
                                      className="accent-primary"
                                      checked={doc.verify}
                                      onChange={(e) => setDocOption(doc.code, { verify: e.target.checked })}
                                    />
                                    Verify
                                  </label>
                                )}
                                <button type="button" onClick={() => removeDoc(doc.code)} className="text-xs text-gray-400 hover:text-red-600">
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => addChildField(opt)}
                        className="h-9 rounded-md border border-primary px-3 text-xs font-medium text-primary hover:bg-primary-50"
                      >
                        + Field
                      </button>
                      {hasDocuments && (
                        <Select className="w-52 text-xs" value="" onChange={(e) => addDoc(opt, e.target.value)} disabled={!availableDocTypes.length}>
                          <option value="">+ Document…</option>
                          {availableDocTypes.map((t) => (
                            <option key={t.code} value={t.code}>
                              {t.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <button type="button" onClick={addOption} className="text-xs text-primary hover:underline">
            + Add option
          </button>
        </div>
      )}

      {(stepLinkSources || registrationLinkOptions) && (
        <StepLinkPanel
          field={field}
          sources={stepLinkSources || []}
          registrationOptions={registrationLinkOptions}
          blocked={children.length > 0 || docsOnThisField.length > 0}
          onChange={replaceSelf}
        />
      )}

      <div className="mt-3 space-y-1.5 border-t border-gray-300 pt-2.5 pl-1">
        <p className="text-xs font-medium text-gray-500">Validation</p>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" className="accent-primary" checked={field.validation.required} onChange={(e) => setRequired(e.target.checked)} />
          Required
        </label>
        {attemptEnabled && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="accent-primary"
              checked={!!field.attempt_field}
              onChange={(e) => replaceSelf({ ...field, attempt_field: e.target.checked })}
            />
            Attempt field
            <span className="text-xs text-gray-400">— saved to history and cleared on every failed attempt</span>
          </label>
        )}

        {field.type === 'text' && !field.linked_registration_field && !stepLink && (
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="accent-primary"
                checked={!!field.validation.format}
                onChange={(e) =>
                  replaceSelf({
                    ...field,
                    validation: { ...field.validation, format: e.target.checked ? { segments: [{ kind: 'letters', length: 3 }] } : null },
                  })
                }
              />
              Answer must follow a format
            </label>
            {field.validation.format && (
              <div className="rounded-md border border-gray-300 bg-white p-2.5">
                <FormatBuilder format={field.validation.format} onChange={(format) => replaceSelf({ ...field, validation: { ...field.validation, format } })} />
              </div>
            )}
          </div>
        )}

        {isDate && (
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="accent-primary" checked={hasDateRule} onChange={(e) => toggleDateRule(e.target.checked)} />
              Date must be relative to today
            </label>
            {hasDateRule && (
              <div className="flex items-center gap-2 pl-6">
                <Select className="w-36" value={field.validation.date_rule.operator} onChange={(e) => setDateRule({ operator: e.target.value })}>
                  {DATE_RULE_OPERATORS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <Select className="w-48" value={field.validation.date_rule.reference} onChange={(e) => setDateRule({ reference: e.target.value })}>
                  {DATE_RULE_REFERENCES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        )}

        {showBasicDetailsOption && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="accent-primary"
              checked={!!field.show_in_basic_details}
              onChange={(e) => replaceSelf({ ...field, show_in_basic_details: e.target.checked })}
            />
            Show in candidate basic details
          </label>
        )}
      </div>
        </>
      )}
    </div>
  );
}
